/**
 * Types aligned with trendy-catalog-service API documentation.
 * List: GET /api/v1/catalog/list (sync status only — no inventory quantities)
 * Sync: GET /api/v1/processCatalogue
 */

import { dashboardBackendUrl } from "../config/gateway";

export type CatalogStatus =
  | "OK"
  | "PARTIAL"
  | "SIESA_CATALOG_MISSING"
  | "PRODUCT_MISSING"
  | "DUPLICATE_PRODUCT"
  | "SKU_MISSING"
  | "SYNC_ERROR";

export interface CatalogVariationItem {
  refId: string;
  manufactureCode: string;
  nameProduct: string;
  skuIdVtex: number | null;
  productIdVtex: number | null;
  statusCatalog: CatalogStatus;
  lastSyncDate: string | null;
}

export interface CatalogParentItem {
  itemId: string;
  manufactureCode: string;
  nameProduct: string;
  productIdVtex: number | null;
  variationCount: number;
  variationsSynced: number;
  variationsMissing: number;
  hasDuplicateProduct: boolean;
  statusCatalog: CatalogStatus;
  lastSyncDate: string | null;
  variations: CatalogVariationItem[];
}

export interface CatalogListResponse {
  totalElements: number;
  totalPages: number;
  currentPage: number;
  pageSize: number;
  paginationMode: string;
  content: CatalogParentItem[];
}

export interface CatalogExportRow {
  tipo: string;
  itemId: string;
  refId: string;
  manufactureCode: string;
  nameProduct: string;
  productIdVtex: string;
  statusCatalog: string;
  variationsSynced: number | string;
  variationsMissing: number | string;
  hasDuplicateProduct: string;
  lastSyncDate: string;
}

export const CATALOG_SEARCH_PARAM = "search";

export const CATALOG_FILTER_TYPE_PARAM = "filterType";

export type CatalogFilterType =
  | "MANUFACTURE_CODE"
  | "NAME_PRODUCT"
  | "PRODUCT_ID_VTEX";

export const CATALOG_FILTER_TYPE_LABELS: Record<CatalogFilterType, string> = {
  MANUFACTURE_CODE: "ID Item SIESA",
  NAME_PRODUCT: "Nombre producto",
  PRODUCT_ID_VTEX: "Product ID VTEX",
};

export const CATALOG_FILTER_TYPE_OPTIONS: {
  value: CatalogFilterType;
  label: string;
}[] = (
  Object.entries(CATALOG_FILTER_TYPE_LABELS) as [CatalogFilterType, string][]
).map(([value, label]) => ({ value, label }));

export const CATALOG_FILTER_TYPE_PLACEHOLDERS: Record<CatalogFilterType, string> = {
  MANUFACTURE_CODE: "Ej: 2216",
  NAME_PRODUCT: "Ej: labial",
  PRODUCT_ID_VTEX: "Ej: 123456",
};

export const CATALOG_API_BASE = "/catalogue-backend";

export const CATALOG_LIST_URL = `${CATALOG_API_BASE}/api/v1/catalog/list`;

export const CATALOG_SYNC_URL = `${CATALOG_API_BASE}/api/v1/processCatalogue`;

export const CATALOG_LOAD_ERROR_MESSAGE =
  "No se pudo cargar el catálogo. Por favor, inténtelo de nuevo en unos minutos.";

/**
 * Maps HTTP failures from GET /catalog/list to a user-facing message.
 */
export const resolveCatalogLoadError = async (
  response: Response
): Promise<string> => {
  let bodyMessage = "";

  try {
    const body = (await response.json()) as {
      message?: string;
      error?: string;
    };
    bodyMessage = body.message ?? body.error ?? "";
  } catch {
    // Non-JSON error bodies are ignored.
  }

  if (response.status === 401 || response.status === 403) {
    return translateCatalogMessage(
      bodyMessage || `unauthorized ${response.status}`,
      "Tu sesión no es válida o expiró. Por favor, inicia sesión nuevamente."
    );
  }

  return translateCatalogMessage(
    bodyMessage || `${response.status} ${response.statusText}`,
    CATALOG_LOAD_ERROR_MESSAGE
  );
};

export const CATALOG_SYNC_ERROR_MESSAGE =
  "La sincronización no pudo completarse. Por favor, inténtelo de nuevo en unos minutos.";

export const CATALOG_VARIATIONS_ERROR_MESSAGE =
  "No se pudieron cargar las variaciones de este producto";

export const CATALOG_SYNC_SUCCESS_MESSAGE =
  "Sincronización de catálogo completada exitosamente.";

export const CATALOG_SYNC_TIMEOUT_MESSAGE =
  "Tiempo de espera agotado. Revise el módulo Logs manualmente.";

export const CATALOG_PROCESS_NOT_CONFIGURED_MESSAGE =
  "Proceso catalogue no configurado en el servidor.";

export const CATALOG_PROCESS_INTEGRATION = "catalogue";

export const CATALOG_POLL_INTERVAL_MS = 10_000;

export const CATALOG_POLL_MAX_WAIT_MS = 60 * 60 * 1000;

export const LOG_PROCESS_URL = dashboardBackendUrl.logProcess();

export const LOG_LOOK_URL = dashboardBackendUrl.logLook();

export interface ProcessDefinition {
  id: number;
  name: string;
  integration: string;
}

export interface CatalogSyncStartResponse {
  status: number;
  message: string;
  response: boolean;
  data: string | null;
}

export interface ProcessLogEntry {
  id: number;
  processName: string;
  executionStatus: string;
  creationDate: string;
  totalItems: number | string;
  failedItems: number;
}

export interface ProcessLogResponse {
  totalElements: number;
  totalPages: number;
  currentPage: number;
  logs?: ProcessLogEntry[];
  content?: ProcessLogEntry[];
}

export type CatalogSyncResultType = "success" | "warning" | "error";

export interface CatalogSyncResult {
  type: CatalogSyncResultType;
  message: string;
  logId?: number;
  /** Process type id needed to open log detail from Catalog. */
  processTypeId?: number;
  totalItems?: number;
  failedItems?: number;
}

export const parseProcessLogList = (data: ProcessLogResponse): ProcessLogEntry[] =>
  data.logs ?? data.content ?? [];

const CATALOG_SYNC_RUNNING_STATUSES = new Set([
  "RUNNING",
  "IN_PROGRESS",
  "PROCESSING",
  "PENDING",
  "STARTED",
  "EXECUTING",
]);

/** ASSUMPTION: Backend marks in-flight jobs with one of the running statuses above. */
export const isCatalogSyncRunningStatus = (status: string): boolean =>
  CATALOG_SYNC_RUNNING_STATUSES.has((status ?? "").toUpperCase());

export const isCatalogSyncTerminalStatus = (status: string): boolean => {
  const normalized = (status ?? "").trim();
  if (!normalized) return false;
  return !isCatalogSyncRunningStatus(normalized);
};

/**
 * Highest log id in the list. Used as baseline before starting a sync so
 * polling can ignore older FAILED/SUCCESSFUL rows.
 */
export const getLatestCatalogLogId = (logs: ProcessLogEntry[]): number =>
  logs.reduce((maxId, log) => Math.max(maxId, log.id), 0);

/**
 * Returns the newest log created after the given baseline id.
 * ASSUMPTION: Backend assigns monotonically increasing log ids per process.
 * Matching by id avoids false FAILED results from clock/timezone skew when
 * comparing server creationDate against client Date.now().
 */
export const findCatalogSyncLogAfter = (
  logs: ProcessLogEntry[],
  baselineLogId: number
): ProcessLogEntry | undefined => {
  const newerLogs = logs.filter((log) => log.id > baselineLogId);
  if (newerLogs.length === 0) return undefined;

  return newerLogs.reduce((latest, log) =>
    log.id > latest.id ? log : latest
  , newerLogs[0]);
};

/** ASSUMPTION: Backend log success statuses are SUCCESSFUL (docs), SUCCESS, or OK. */
const CATALOG_SYNC_SUCCESS_STATUSES = new Set([
  "SUCCESSFUL",
  "SUCCESS",
  "OK",
]);

const isCatalogSyncSuccessStatus = (status: string): boolean =>
  CATALOG_SYNC_SUCCESS_STATUSES.has(status);

export const buildCatalogSyncResult = (log: ProcessLogEntry): CatalogSyncResult => {
  const status = (log.executionStatus ?? "").toUpperCase();
  const failedItems = log.failedItems ?? 0;
  const totalItems =
    typeof log.totalItems === "number"
      ? log.totalItems
      : Number.parseInt(String(log.totalItems ?? ""), 10);

  const base = {
    logId: log.id,
    totalItems: Number.isNaN(totalItems) ? undefined : totalItems,
    failedItems,
  };

  const itemsSummary =
    base.totalItems != null
      ? ` Procesados: ${base.totalItems}${
          failedItems > 0 ? `, con errores: ${failedItems}` : ""
        }.`
      : failedItems > 0
        ? ` Ítems con error: ${failedItems}.`
        : "";

  if (isCatalogSyncSuccessStatus(status) && failedItems === 0) {
    return {
      ...base,
      type: "success",
      message: `Sincronización finalizada correctamente.${
        base.totalItems != null ? ` Items procesados: ${base.totalItems}.` : ""
      }`,
    };
  }

  const allItemsFailed =
    base.totalItems != null &&
    base.totalItems > 0 &&
    failedItems >= base.totalItems;

  // ASSUMPTION: Backend may return FAILED when any item fails, even if other
  // items synced successfully. Treat that as completed with item-level issues.
  const completedWithItemErrors =
    status === "PARTIAL" ||
    (isCatalogSyncSuccessStatus(status) && failedItems > 0) ||
    (status === "FAILED" && !allItemsFailed);

  if (completedWithItemErrors) {
    return {
      ...base,
      type: "warning",
      message:
        `La sincronización se completó, pero algunos ítems presentaron errores.${itemsSummary}`,
    };
  }

  if (status === "FAILED" && allItemsFailed) {
    return {
      ...base,
      type: "error",
      message:
        `La sincronización no pudo completar ningún ítem correctamente.${itemsSummary}`,
    };
  }

  return {
    ...base,
    type: "error",
    message: `La sincronización finalizó con un estado inesperado (${
      log.executionStatus || "desconocido"
    }).`,
  };
};

/**
 * Translates backend messages when needed. Spanish messages from the API are kept as-is.
 */
const CATALOG_MESSAGE_TRANSLATIONS: { match: RegExp; es: string }[] = [
  {
    match: /currently unavailable|service unavailable|unavailable|try again later|503/i,
    es: "El servicio de catálogo no está disponible en este momento. Por favor, inténtelo de nuevo más tarde.",
  },
  {
    match: /failed to fetch|network ?error|load failed|connection/i,
    es: "No se pudo conectar con el servidor. Verifica tu conexión e inténtalo de nuevo.",
  },
  {
    match: /unauthorized|forbidden|not authenticated|invalid token|token|401|403/i,
    es: "Tu sesión no es válida o expiró. Por favor, inicia sesión nuevamente.",
  },
  {
    match: /timeout|timed out|gateway timeout|504/i,
    es: "La operación tardó demasiado. Por favor, inténtelo de nuevo.",
  },
  {
    match: /already (running|in progress)|in progress/i,
    es: "Ya hay una sincronización en curso. Espera a que finalice antes de iniciar otra.",
  },
  {
    match: /internal server error|unexpected error|500/i,
    es: "Ocurrió un error en el servidor. Por favor, inténtelo de nuevo en unos minutos.",
  },
  {
    match: /success|successfully|completed|processed|done/i,
    es: CATALOG_SYNC_SUCCESS_MESSAGE,
  },
];

export const translateCatalogMessage = (
  raw: string,
  fallback: string = CATALOG_SYNC_ERROR_MESSAGE
): string => {
  const text = (raw ?? "").trim();
  if (!text) return fallback;

  // Backend messages are already in Spanish — preserve them.
  if (/[áéíóúñ¿¡]/i.test(text)) return text;

  for (const { match, es } of CATALOG_MESSAGE_TRANSLATIONS) {
    if (match.test(text)) return es;
  }

  return fallback;
};

export const CATALOG_STATUS_LABELS: Record<CatalogStatus, string> = {
  OK: "Sincronizado",
  PARTIAL: "Parcial",
  SIESA_CATALOG_MISSING: "Sin catálogo SIESA",
  PRODUCT_MISSING: "Producto faltante",
  DUPLICATE_PRODUCT: "Duplicado",
  SKU_MISSING: "SKU faltante",
  SYNC_ERROR: "Error de sync",
};

export const mapCatalogListResponse = (data: unknown): CatalogListResponse => {
  const payload = data as CatalogListResponse;

  if (!payload || !Array.isArray(payload.content)) {
    throw new Error("Formato de respuesta inesperado del catálogo.");
  }

  return {
    totalElements: payload.totalElements ?? 0,
    totalPages: payload.totalPages ?? 0,
    currentPage: payload.currentPage ?? 1,
    pageSize: payload.pageSize ?? 20,
    paginationMode: payload.paginationMode ?? "BY_PARENT",
    content: payload.content,
  };
};
